/* RENTA PODLE TABULEK FICHTNER — podíl akcií a portfolio po letech čerpání
 * ============================================================================
 * Majitel 18. 10. 2026 („určitě je to super! jejich metodika“), metodika
 * 2026-10-18. Zdroj: 2026-01-01-metodika-prace-s-klientem-bez-ID.pdf,
 * s. 26 (příklad renty 10 000 Kč/měs po letech čerpání) a s. 27 (podíl akcií
 * renty podle let do začátku čerpání), přepis docs/metodika-alokace-fichtner.md.
 *
 * 1) PŘED ČERPÁNÍM (akumulace renty): podíl akcií z tabulky s. 27 podle let
 *    do začátku čerpání (0–15, nad 15 sloupec 15). Zbytek do 100 se dělí mezi
 *    dluhopisy a hotovost v poměru portfolia v čerpání (sloupec 0) — PDF to
 *    pro roky 1–15 neuvádí, je to NÁŠ dopočet (rozhodnutí (c) F9).
 * 2) V ČERPÁNÍ (sloupec 0, graf nekonečné renty s. 26): konzervativní
 *    33/41/26, vyvážená 48/26/26 (v PDF 48/25/26 = 99 %, rozhodnutí 5 F9),
 *    růstová 67/15/18.
 * 3) PORTFOLIO PO LETECH ČERPÁNÍ (s. 26): peníze na rentu roku n leží podle
 *    tabulky pro cíl za n let (s. 25); dnes na ně stačí renta / (1 + r)^n.
 *    Kapitál renty plán DÁL počítá pravidlem 4 % (doporučení 2 F9), tahle
 *    funkce je jen pro výklad a srovnání.
 *
 * Modul je BEZ ZÁVISLOSTÍ mimo lib/alokaceTabulky (čte ho planSolver).
 */
import { alokaceZTabulky, profilTabulky } from "./alokaceTabulky";

/** Tabulka s. 27: podíl akcií renty v % — index = roky do začátku čerpání (0–15). */
export const RENTA_AKCIE = Object.freeze({
  konzervativni: [33, 35, 38, 40, 42, 44, 46, 47, 48, 49, 49, 49, 50, 50, 50, 50],
  vyvazeny: [48, 51, 54, 57, 59, 62, 65, 68, 71, 73, 76, 78, 81, 83, 86, 88],
  rustovy: [67, 71, 76, 80, 84, 87, 90, 92, 94, 96, 97, 98, 98, 99, 99, 99],
});

/** Portfolio renty v čerpání (sloupec 0, s. 26) — akcie/dluhopisy/hotovost v %. */
export const RENTA_CERPANI = Object.freeze({
  konzervativni: Object.freeze({ akcie: 33, dluhopisy: 41, cash: 26 }),
  vyvazeny: Object.freeze({ akcie: 48, dluhopisy: 26, cash: 26 }),
  rustovy: Object.freeze({ akcie: 67, dluhopisy: 15, cash: 18 }),
});

/**
 * Alokace renty `roky` let před začátkem čerpání (0 = už se čerpá).
 * 0, záporné, NaN, prázdné → 0 (čerpání); nad 15 i Infinity → 15.
 * @returns {{akcie: number, dluhopisy: number, cash: number}} procenta, součet 100
 */
export function alokaceRenty(roky, profil = "vyvazeny") {
  const p = profilTabulky(profil);
  const n = +roky;
  const sloupec = n === Infinity ? 15 : Math.max(0, Math.min(15, Math.round(Number.isFinite(n) ? n : 0)));
  const akcie = RENTA_AKCIE[p][sloupec];
  const c0 = RENTA_CERPANI[p];
  const zbytek = 100 - akcie;
  const dluhopisy = Math.round((zbytek * c0.dluhopisy) / (c0.dluhopisy + c0.cash));
  return { akcie, dluhopisy, cash: zbytek - dluhopisy };
}

/**
 * Portfolio renty po letech čerpání (s. 26): kolik dnes odložit na rentu
 * roku 0…roky a jak to rozložit.
 * @param {{ rentaRocne: number, roky: number, profil?: string,
 *           vynos: (alok: {akcie, dluhopisy, cash}, rok: number) => number }} o
 *   `vynos` = roční výnos jako ZLOMEK (0.0338 = 3,38 %) pro alokaci roku;
 *   volající dodá svou metodiku výnosů (plán: planSolver.vynosPortfolia).
 * @returns {{ radky: Array<{rok, alokace, vynos, dnes, castky: {akcie, dluhopisy, cash}}>, celkem: number }}
 */
export function rentaPoLetech({ rentaRocne, roky, profil = "vyvazeny", vynos } = {}) {
  const renta = Number.isFinite(+rentaRocne) ? Math.max(0, +rentaRocne) : 0;
  const N = Number.isFinite(+roky) ? Math.max(0, Math.min(100, Math.round(+roky))) : 0;
  const radky = [];
  let celkem = 0;
  for (let rok = 0; rok <= N; rok++) {
    const alokace = rok === 0 ? { akcie: 0, dluhopisy: 0, cash: 100 } : alokaceZTabulky(rok, profil);
    const r = typeof vynos === "function" ? +vynos(alokace, rok) : 0;
    const rr = Number.isFinite(r) && r > -1 ? r : 0;
    const dnes = renta / Math.pow(1 + rr, rok);
    radky.push({
      rok, alokace, vynos: rr, dnes,
      castky: { akcie: (dnes * alokace.akcie) / 100, dluhopisy: (dnes * alokace.dluhopisy) / 100, cash: (dnes * alokace.cash) / 100 },
    });
    celkem += dnes;
  }
  return { radky, celkem };
}
